import React, { useState, useEffect } from 'react';
import styles from './ContactDetail.module.css';
import { ContactModal } from './ContactModal';
import { ContactMomentModal } from './ContactMomentModal';
import { Contact, ContactMoment } from './types';

interface ContactDetailProps {
  contact: Contact;
  contactMoments: ContactMoment[];
  onClose: () => void;
  onAddMoment: (moment: any) => void;  // Accept any type since backend expects different format
  onEditContact: (contact: Contact) => void;
  onDelete?: () => void;
}

export const ContactDetail: React.FC<ContactDetailProps> = ({
  contact,
  contactMoments,
  onClose,
  onAddMoment,
  onEditContact,
  onDelete
}) => {
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'actions'>('info');
  const [showAddMomentModal, setShowAddMomentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMoment, setEditingMoment] = useState<ContactMoment | undefined>(undefined);
  const [localContactMoments, setLocalContactMoments] = useState<ContactMoment[]>(contactMoments);

  // Update local contact moments when prop changes
  useEffect(() => {
    setLocalContactMoments(contactMoments);
  }, [contactMoments]);

  const relationshipColors = {
    excellent: '#4CAF50',
    good: '#277CEA',
    neutral: '#9DA6A8',
    needs_attention: '#E6007E'
  };

  const relationshipLabels = {
    excellent: 'Uitstekend',
    good: 'Goed',
    neutral: 'Neutraal',
    needs_attention: 'Aandacht nodig'
  };

  const typeIcons = {
    meeting: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    phone: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    email: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    event: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    social_media: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2.2C22 1.7 22 1.7 21.5 9.7C21.3 12.2 21 14 20.5 15.5C19.7 18.5 18 20 15.1 20.8C13.5 21.3 12.2 21.5 9.8 21.7C2.3 22.2 2.3 22.2 2.7 14.7L2.8 13.2C3 11 3.2 9.3 3.7 7.7C4.7 4.3 6.7 2.7 9.7 2.1L14 2.2Z" />
      </svg>
    ),
    letter: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-10 5L2 7" />
      </svg>
    ),
    other: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    )
  };

  const typeLabels: Record<string, string> = {
    meeting: 'Bijeenkomst',
    phone: 'Telefonisch',
    call: 'Telefonisch',
    email: 'E-mail',
    event: 'Evenement',
    social_media: 'Social Media',
    letter: 'Brief',
    other: 'Overig'
  };


  // Derive relationship quality from support level if not set
  const relationshipQuality = contact.relationshipQuality ||
    (contact.supportLevel === 'supporter' || contact.role === 'supporter' ? 'good' :
     contact.supportLevel === 'opponent' || contact.role === 'opponent' ? 'needs_attention' :
     'neutral');

  return (
    <>
      {/* Backdrop */}
      <div
        className={styles.backdrop}
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999
        }}
      />

      {/* Modal */}
      <div className={styles.contactDetail}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerInfo}>
            <h1>{contact.name}</h1>
            <p className={styles.roleText}>{contact.role || 'Stakeholder'} • {contact.organization?.name || 'Particulier'}</p>
            <div className={styles.headerTags}>
              {contact.tags && contact.tags.map(tag => (
                <span key={tag} className={styles.tag}>{tag}</span>
              ))}
            </div>
          </div>
          <div className={styles.headerActions}>
            <div
              className={styles.relationshipBadge}
              style={{ backgroundColor: relationshipColors[relationshipQuality] }}
            >
              {relationshipLabels[relationshipQuality]}
            </div>
            <button className={styles.closeBtn} onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className={styles.quickActions}>
          <button className={styles.quickActionBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Email sturen
          </button>
          <button className={styles.quickActionBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            Bellen
          </button>
          <button className={styles.quickActionBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Afspraak plannen
          </button>
          <button
            className={styles.quickActionBtn}
            onClick={() => setShowEditModal(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Bewerken
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'info' ? styles.active : ''}`}
          onClick={() => setActiveTab('info')}
        >
          Informatie
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Contactgeschiedenis ({localContactMoments.length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'actions' ? styles.active : ''}`}
          onClick={() => setActiveTab('actions')}
        >
          Acties & Notities
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'info' && (
          <div className={styles.infoTab}>
            <div className={styles.infoGrid}>
                <div className={styles.infoSection}>
                  <h3>Contactgegevens</h3>
                  <div className={styles.infoItems}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Email</span>
                      <a href={`mailto:${contact.contact?.email}`}>{contact.contact?.email}</a>
                    </div>
                    {contact.contact?.phone && (
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Telefoon</span>
                        <a href={`tel:${contact.contact?.phone}`}>{contact.contact?.phone}</a>
                      </div>
                    )}
                    {contact.contact?.address?.street && (
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Adres</span>
                        <span>
                          {contact.contact.address.street}
                          {contact.contact.address.zipCode || contact.contact.address.city ? (
                            <>, {[contact.contact.address.zipCode, contact.contact.address.city].filter(Boolean).join(' ')}</>
                          ) : null}
                        </span>
                      </div>
                    )}
                    {contact.social?.website && (
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Website</span>
                        <a href={contact.social.website} target="_blank" rel="noopener noreferrer">
                          {contact.social.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.infoSection}>
                  <h3>Organisatie</h3>
                  <div className={styles.infoItems}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Bedrijf/Instantie</span>
                      <span>{contact.organization?.name || 'Particulier'}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Functie</span>
                      <span>{contact.role || 'Stakeholder'}</span>
                    </div>
                  </div>
                </div>

                {contact.lastContact && (
                  <div className={styles.infoSection}>
                    <h3>Laatste interactie</h3>
                    <div className={styles.infoItems}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Datum</span>
                        <span>{new Date(contact.lastContact).toLocaleDateString('nl-NL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className={styles.historyTab}>
            <div className={styles.historyHeader}>
              <h3>Contactmomenten</h3>
              <button
                className={styles.btnPrimary}
                onClick={() => {
                  setEditingMoment(undefined);
                  setShowAddMomentModal(true);
                }}
              >
                + Nieuw moment
              </button>
            </div>

            <div className={styles.timeline}>
              {localContactMoments.length > 0 ? (
                localContactMoments
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map(moment => (
                    <div key={`${moment.date}-${moment.type}`} className={styles.timelineItem}>
                      <div className={styles.timelineIcon}>
                        {(typeIcons as any)[moment.type] || typeIcons.other}
                      </div>
                      <div className={styles.timelineContent}>
                        <div className={styles.timelineHeader}>
                          <h4>{moment.subject}</h4>
                          <span className={styles.timelineDate}>
                            {new Date(moment.date).toLocaleDateString('nl-NL')}
                          </span>
                        </div>
                        <span className={styles.timelineType}>{typeLabels[moment.type]}</span>
                        {moment.description && (
                          <p className={styles.timelineNotes}>{moment.description}</p>
                        )}
                        {moment.followUpRequired && (
                          <div className={styles.timelineFollowUp}>
                            <strong>Opvolging vereist</strong>
                          </div>
                        )}
                        {moment.participants && moment.participants.length > 0 && (
                          <div className={styles.timelineParticipants}>
                            <strong>Aanwezig:</strong> {moment.participants.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
              ) : (
                <div className={styles.emptyState}>
                  <p>Nog geen contactmomenten geregistreerd</p>
                  <button
                    className={styles.btnPrimary}
                    onClick={() => {
                      setEditingMoment(undefined);
                      setShowAddMomentModal(true);
                    }}
                  >
                    Eerste moment toevoegen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'actions' && (
          <div className={styles.actionsTab}>
            <div className={styles.actionSection}>
              <h3>Volgende acties</h3>
              {contact.nextAction ? (
                <div className={styles.actionCard}>
                  <div className={styles.actionIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                    </svg>
                  </div>
                  <div className={styles.actionContent}>
                    <p>{contact.nextAction}</p>
                    <button className={styles.btnSmall}>Markeer als voltooid</button>
                  </div>
                </div>
              ) : (
                <p className={styles.emptyText}>Geen geplande acties</p>
              )}
            </div>

            <div className={styles.actionSection}>
              <h3>Notities</h3>
              {contact.notes ? (
                <div className={styles.notesBox}>
                  <p>{contact.notes}</p>
                </div>
              ) : (
                <p className={styles.emptyText}>Geen notities beschikbaar</p>
              )}
            </div>

            <div className={styles.actionSection}>
              <h3>Documenten</h3>
              <div className={styles.documentsList}>
                <p className={styles.emptyText}>Nog geen documenten gekoppeld</p>
                <button className={styles.btnSecondary}>
                  + Document toevoegen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Contact Modal */}
      {showEditModal && (
        <ContactModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={(updatedContact) => {
            onEditContact(updatedContact);
            setShowEditModal(false);
          }}
          onDelete={onDelete}
          contact={contact}
          projectId={contact.project}
        />
      )}

      {/* Add/Edit Contact Moment Modal */}
      {showAddMomentModal && (
        <ContactMomentModal
          isOpen={showAddMomentModal}
          onClose={() => {
            setShowAddMomentModal(false);
            setEditingMoment(undefined);
          }}
          onSave={(moment) => {
            // Add the new moment to local state immediately for instant feedback
            const newMoment = {
              ...moment,
              _id: `temp-${Date.now()}`, // Temporary ID until backend response
            };
            setLocalContactMoments([newMoment, ...localContactMoments]);

            // Also switch to history tab to show the new moment
            setActiveTab('history');

            // Call the parent handler to save to backend
            onAddMoment(moment);
            setShowAddMomentModal(false);
            setEditingMoment(undefined);
          }}
          moment={editingMoment}
          contactId={contact._id}
        />
      )}
    </div>
    </>
  );
};